import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Trash2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { QrCard } from "@/components/admin/MenuQrCard";
import { Stars } from "@/components/MyReviews";

export default function StaffReviewsManager() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [qr, setQr] = useState(null);

  const load = () =>
    adminApi.get("/admin/staff-reviews").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  async function remove(r) {
    if (!window.confirm("Delete this review?")) return;
    await adminApi.delete(`/admin/staff-reviews/${r.id}`);
    toast.success("Review deleted");
    load();
  }

  if (!data) return null;
  const list = data.reviews.filter((r) => filter === "all" || r.staff_id === filter);

  return (
    <div data-testid="staff-reviews-manager" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tight">Crew Reviews</h2>
          <p className="text-sm text-[var(--trio-muted)]">Guests scan a crew member's "Rate Me" QR. These stay private and never show on the public site.</p>
        </div>
        <div className="text-right" data-testid="staff-reviews-overall">
          <div className="font-display text-4xl text-[var(--trio-primary)]">{data.average ?? "—"}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--trio-muted)]">{data.count} reviews</div>
        </div>
      </div>
      <div className="overflow-x-auto border border-[var(--trio-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-[0.25em] uppercase text-[var(--trio-muted)] text-left bg-[var(--trio-surface)]">
              <th className="px-4 py-2">Crew</th><th className="px-2 py-2">Avg</th><th className="px-2 py-2 text-right">Reviews</th>
              <th className="px-2 py-2 text-right">5★</th><th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.staff.map((s) => (
              <tr key={s.id} data-testid={`staff-review-row-${s.referral_code}`} className="border-t border-[var(--trio-border)]">
                <td className="px-4 py-2 font-semibold">{s.name}</td>
                <td className="px-2 py-2">{s.average ? <span className="inline-flex items-center gap-2"><Stars n={Math.round(s.average)} /> {s.average}</span> : "—"}</td>
                <td className="px-2 py-2 text-right">{s.count}</td>
                <td className="px-2 py-2 text-right">{s.five_stars}</td>
                <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => setFilter(s.id)} data-testid={`staff-review-filter-${s.referral_code}`} className="text-xs uppercase tracking-widest border border-[var(--trio-border)] px-3 py-1">View</button>
                  {s.referral_code && (
                    <button onClick={() => setQr(s)} data-testid={`staff-review-qr-${s.referral_code}`} className="text-xs uppercase tracking-widest border border-[var(--trio-border)] px-3 py-1 inline-flex items-center gap-1"><QrCode className="w-3 h-3" /> Rate-Me QR</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-[var(--trio-muted)]">
          Showing: {filter === "all" ? "Everyone" : data.staff.find((s) => s.id === filter)?.name}
        </span>
        {filter !== "all" && <button onClick={() => setFilter("all")} data-testid="staff-review-filter-all" className="text-xs underline">Show all</button>}
      </div>
      <ul className="divide-y divide-[var(--trio-border)] border border-[var(--trio-border)] bg-[var(--trio-surface)]">
        {list.map((r) => (
          <li key={r.id} data-testid={`admin-review-${r.id}`} className="p-4 flex gap-4 justify-between">
            <div>
              <div className="flex items-center gap-3"><Stars n={r.rating} /><span className="text-xs font-semibold">for {r.staff_name}</span></div>
              {r.comment && <p className="text-sm mt-2">{r.comment}</p>}
              <div className="text-xs text-[var(--trio-muted)] mt-1">— {r.name || "Guest"} · {new Date(r.created_at).toLocaleString()}</div>
            </div>
            <button onClick={() => remove(r)} data-testid={`admin-review-delete-${r.id}`} className="text-red-700 p-2 self-start" title="Delete"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
        {list.length === 0 && <li className="p-6 text-center text-sm text-[var(--trio-muted)]">No reviews yet.</li>}
      </ul>
      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl rounded-none p-0 border-0">
          <DialogTitle className="sr-only">Rate-Me QR</DialogTitle>
          {qr && (
            <QrCard
              url={`${window.location.origin}/review/${qr.referral_code}`}
              label={`${qr.name}'s Rate-Me QR`}
              title={`Rate ${qr.name.split(" ")[0]}`}
              description="Guests scan this to leave a star rating and comment for this crew member."
              printHeading="How Was Your Server?"
              filename={`trio-review-${qr.referral_code}.png`}
              testid="admin-review-qr-card"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
